import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Move } from 'lucide-react';

const STORAGE_KEY = 'derwent_meeting_pip_pos';
const HANDLE_OFFSET = 8;
const SNAP_GAP = 16;          // distance from each edge when snapped
const SNAP_BAR_OFFSET = 110;  // controls bar height — keep snapped corners above it
const DOUBLE_TAP_MS = 320;    // max ms between two taps to count as double
const DRAG_THRESHOLD_PX = 4;  // pointer movement that counts as a drag, not a tap

/**
 * DraggablePiP — wraps a local-video tile in a freely draggable container.
 *
 * Why custom (no library): we don't want react-draggable just for one
 * 50-line behaviour, and we need it touch-aware for iOS. Keeps the bundle
 * lean.
 *
 * Behaviour:
 *  • Default position = bottom-right (above the controls bar).
 *  • User drags the camera anywhere within the viewport.
 *  • Position persists in localStorage so the camera stays where the broker
 *    parked it across calls.
 *  • Window resize clamps the saved position to keep it on-screen.
 */
const clampToViewport = (x, y, width, height) => {
  const maxX = Math.max(0, window.innerWidth - width - HANDLE_OFFSET);
  const maxY = Math.max(0, window.innerHeight - height - HANDLE_OFFSET);
  return {
    x: Math.min(Math.max(HANDLE_OFFSET, x), maxX),
    y: Math.min(Math.max(HANDLE_OFFSET, y), maxY),
  };
};

const loadInitialPos = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (typeof p?.x === 'number' && typeof p?.y === 'number') return p;
  } catch (e) { /* corrupt — ignore */ }
  return null;
};

const DraggablePiP = ({ children, defaultCorner = 'br', size = { w: 224, h: 126 }, testId }) => {
  const containerRef = useRef(null);
  const dragState = useRef({ active: false, dx: 0, dy: 0, startX: 0, startY: 0, moved: false });
  const lastTapRef = useRef(0);
  const [pos, setPos] = useState(() => {
    const saved = loadInitialPos();
    if (saved) return saved;
    if (typeof window === 'undefined') return { x: 16, y: 16 };
    if (defaultCorner === 'br') {
      return { x: window.innerWidth - size.w - SNAP_GAP, y: window.innerHeight - size.h - SNAP_BAR_OFFSET };
    }
    return { x: SNAP_GAP, y: SNAP_GAP };
  });
  const [hovered, setHovered] = useState(false);
  // While a snap-animation is in flight we add a CSS transition; during
  // active drag we strip it so the tile follows the cursor 1:1.
  const [animating, setAnimating] = useState(false);

  /**
   * Springs the PiP to the corner closest to the current centre. Used by
   * the double-tap gesture so brokers can get the camera out of the way
   * with one action without dragging back to a corner.
   */
  const snapToNearestCorner = useCallback(() => {
    const cx = pos.x + size.w / 2;
    const cy = pos.y + size.h / 2;
    const left = cx < window.innerWidth / 2;
    const top = cy < window.innerHeight / 2;
    const target = {
      x: left ? SNAP_GAP : window.innerWidth - size.w - SNAP_GAP,
      // Keep snapped corners above the controls bar at the bottom.
      y: top ? SNAP_GAP : window.innerHeight - size.h - SNAP_BAR_OFFSET,
    };
    setAnimating(true);
    setPos(target);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(target)); } catch (e) { /* */ }
    // Clear the transition class after it finishes so the next drag is snappy.
    setTimeout(() => setAnimating(false), 280);
  }, [pos.x, pos.y, size.w, size.h]);

  // Re-clamp on viewport resize so the PiP doesn't end up off-screen
  useEffect(() => {
    const onResize = () => {
      setPos((p) => clampToViewport(p.x, p.y, size.w, size.h));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [size.w, size.h]);

  const onPointerDown = useCallback((e) => {
    if (!containerRef.current) return;
    if (e.button != null && e.button !== 0) return;
    const rect = containerRef.current.getBoundingClientRect();
    const clientX = e.clientX != null ? e.clientX : (e.touches && e.touches[0]?.clientX);
    const clientY = e.clientY != null ? e.clientY : (e.touches && e.touches[0]?.clientY);
    if (clientX == null || clientY == null) return;
    dragState.current = {
      active: true,
      dx: clientX - rect.left,
      dy: clientY - rect.top,
      startX: clientX,
      startY: clientY,
      moved: false,
    };
    setAnimating(false);
    try { containerRef.current.setPointerCapture(e.pointerId); } catch (err) { /* */ }
    e.preventDefault();
  }, []);

  const onPointerMove = useCallback((e) => {
    if (!dragState.current.active) return;
    const clientX = e.clientX != null ? e.clientX : (e.touches && e.touches[0]?.clientX);
    const clientY = e.clientY != null ? e.clientY : (e.touches && e.touches[0]?.clientY);
    if (clientX == null || clientY == null) return;
    if (!dragState.current.moved) {
      const dx = clientX - dragState.current.startX;
      const dy = clientY - dragState.current.startY;
      if (Math.abs(dx) + Math.abs(dy) > DRAG_THRESHOLD_PX) dragState.current.moved = true;
    }
    const next = clampToViewport(
      clientX - dragState.current.dx,
      clientY - dragState.current.dy,
      size.w,
      size.h,
    );
    setPos(next);
  }, [size.w, size.h]);

  const onPointerUp = useCallback(() => {
    if (!dragState.current.active) return;
    const wasDrag = dragState.current.moved;
    dragState.current.active = false;
    if (wasDrag) {
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(pos)); } catch (e) { /* */ }
      lastTapRef.current = 0;
      return;
    }
    // Tap (no movement) — count toward double-tap detection. Two taps in
    // quick succession snap the PiP to the nearest corner. Works for both
    // mouse double-click and finger double-tap.
    const now = Date.now();
    if (now - lastTapRef.current < DOUBLE_TAP_MS) {
      lastTapRef.current = 0;
      snapToNearestCorner();
    } else {
      lastTapRef.current = now;
    }
  }, [pos, snapToNearestCorner]);

  return (
    <div
      ref={containerRef}
      className={`absolute z-40 cursor-grab active:cursor-grabbing select-none touch-none ${
        animating ? 'transition-[left,top] duration-300 ease-out' : ''
      }`}
      style={{
        left: `${pos.x}px`,
        top: `${pos.y}px`,
        width: `${size.w}px`,
        height: `${size.h}px`,
      }}
      data-testid={testId}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onDoubleClick={(e) => {
        // Mouse fallback: setHovered noise + ensures snap also triggers
        // even when our pointer-tap logic missed (e.g. dev tools).
        e.preventDefault();
        snapToNearestCorner();
      }}
    >
      {/* Subtle drag-affordance overlay (visible on hover) */}
      <div
        className={`absolute inset-0 rounded-lg ring-2 transition-all pointer-events-none ${
          hovered ? 'ring-emerald-400/60 shadow-2xl' : 'ring-white/10'
        }`}
      />
      <div
        className={`absolute top-1.5 left-1.5 z-10 inline-flex items-center gap-1 rounded-md bg-slate-950/70 backdrop-blur px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-emerald-300 transition-opacity ${
          hovered ? 'opacity-100' : 'opacity-0'
        }`}
      >
        <Move className="h-3 w-3" /> drag · double-tap to snap
      </div>
      {/* Children = the <video> tile */}
      {children}
    </div>
  );
};

export default DraggablePiP;
